import api from './api';

/**
 * Credit Service
 * Handles all credit-related API calls
 * Phase 7: Credit System
 */
const creditService = {
  /**
   * Get current balance for user or company
   * @param {string} userId - (Optional) User ID for admin queries
   * @param {string} companyId - (Optional) Company ID for admin queries
   * @returns {Promise<{balance: number}>}
   */
  getBalance: async (userId = null, companyId = null) => {
    const params = {};
    if (userId) params.userId = userId;
    if (companyId) params.companyId = companyId;
    
    const response = await api.get('/credits/balance', { params });
    return response.data.data; // { balance }
  },

  /**
   * Check balance for required credits (document wizard pre-check)
   * @param {number} requiredCredits - Required credit amount (can be decimal: 1.5, 0.7)
   * @returns {Promise<{hasEnough: boolean, currentBalance: number, required: number, deficit: number}>}
   */
  checkBalance: async (requiredCredits) => {
    const response = await api.get('/credits/check-balance', {
      params: { requiredCredits }
    });
    return response.data.data;
  },

  /**
   * Get transaction history for user or company
   * @param {Object} params - {page, limit, type, startDate, endDate, userId, companyId}
   * @returns {Promise<{transactions: [], total: number, page: number, limit: number}>}
   */
  getTransactionHistory: async (params = {}) => {
    const { page, limit, type, startDate, endDate, userId, companyId } = params;
    const queryParams = new URLSearchParams();
    
    if (page) queryParams.append('page', page);
    if (limit) queryParams.append('limit', limit);
    if (type) queryParams.append('type', type);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    if (userId) queryParams.append('userId', userId);
    if (companyId) queryParams.append('companyId', companyId);
    
    const response = await api.get(`/credits/history?${queryParams}`);
    return response.data.data; // { transactions, total, page, limit }
  },

  /**
   * Purchase credits (mock payment)
   * @param {string} packageId - Package ID
   * @param {Object} paymentData - {cardNumber, expiryDate, cvv, cardHolder}
   * @param {string} source - Source of purchase: 'page' | 'wizard' (default: 'page')
   * @returns {Promise<{transaction: CreditTransaction, balance: number}>}
   */
  purchaseCredits: async (packageId, paymentData, source = 'page') => {
    const response = await api.post('/credits/purchase', {
      packageId,
      paymentData,
      source
    });
    return response.data.data; // { transaction, balance }
  },

  /**
   * Add manual credits (admin only)
   * @param {Object} data - {userId?, companyId?, amount, description}
   * @returns {Promise<{transaction: CreditTransaction, balance: number}>}
   */
  addManualCredits: async (data) => {
    const response = await api.post('/credits/manual/add', data);
    return response.data.data; // { transaction, balance }
  },

  /**
   * Subtract manual credits (admin only)
   * @param {Object} data - {userId?, companyId?, amount, description}
   * @returns {Promise<{transaction: CreditTransaction, balance: number}>}
   */
  subtractManualCredits: async (data) => {
    const response = await api.post('/credits/manual/subtract', data);
    return response.data.data; // { transaction, balance }
  },

  /**
   * Get credit reports (permission-based scope)
   * @param {Object} params - {startDate, endDate}
   * @returns {Promise<{totalSales: number, totalUsed: number, documents: [], users: []}>}
   */
  getReports: async (params = {}) => {
    const { startDate, endDate } = params;
    const queryParams = new URLSearchParams();
    
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    
    const response = await api.get(`/credits/reports?${queryParams}`);
    return response.data.data; // { totalSales, totalUsed, documents, users }
  },

  /**
   * Get billing history (purchase transactions only)
   * @param {Object} params - {startDate, endDate}
   * @returns {Promise<{transactions: [], total: number, summary: {totalSpent: number, totalCredits: number}}>}
   */
  getBillingHistory: async (params = {}) => {
    const { startDate, endDate } = params;
    const queryParams = new URLSearchParams();
    
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    
    const response = await api.get(`/billing/billing-history?${queryParams}`);
    return response.data.data; // { transactions, total, summary }
  }
};

export default creditService;

